import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { 
  CheckCircle, 
  AlertCircle, 
  Shield, 
  User, 
  Briefcase, 
  Building,
  Loader2,
  QrCode
} from 'lucide-react';

const VerifyEmployee = () => {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const verifyCode = async () => {
      const code = searchParams.get('code');
      
      if (!code) {
        setError('No verification code provided');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(`${API_URL}/api/employees/qr-verify`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ encrypted_data: decodeURIComponent(code) }),
        });

        if (response.ok) {
          const data = await response.json();
          setResult(data);
        } else {
          const err = await response.json();
          setError(err.detail || 'Verification failed');
        }
      } catch (err) {
        console.error('Verification error:', err);
        setError('Failed to verify employee. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    verifyCode();
  }, [searchParams, API_URL]);

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0779BC]/5 to-[#0779BC]/10 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-12 w-12 text-[#0779BC] animate-spin mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900">Verifying Employee...</h2>
            <p className="text-gray-500 mt-2">Please wait while we verify the employee information.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-red-200">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-red-900">Verification Failed</h2>
            <p className="text-red-600 mt-2">{error}</p>
            <p className="text-gray-500 mt-4 text-sm">
              The QR code may be invalid, expired, or tampered with.
            </p>
            <Button 
              variant="outline" 
              className="mt-6"
              onClick={() => window.location.href = '/'}
            >
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (result && result.verified) {
    const employee = result.employee;
    
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0779BC]/5 to-[#0779BC]/10 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-[#0779BC]/20 shadow-lg">
          <CardContent className="p-0">
            {/* Header */}
            <div className="bg-[#0779BC] p-6 rounded-t-lg text-center">
              <div className="w-20 h-20 bg-white rounded-full flex items-center justify-center mx-auto mb-3 shadow-lg">
                <CheckCircle className="h-12 w-12 text-[#0779BC]" />
              </div>
              <h2 className="text-2xl font-bold text-white">Verified Employee</h2>
              <Badge className="mt-2 bg-white/20 text-white border-0">
                <Shield className="h-3 w-3 mr-1" />
                Authentic ID
              </Badge>
            </div>
            
            {/* Employee Info */}
            <div className="p-6 space-y-4">
              {/* Name */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-[#0779BC]/10 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-[#0779BC]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Full Name</p>
                  <p className="text-lg font-semibold text-gray-900">{employee.name}</p>
                </div>
              </div>
              
              {/* Employee ID */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-[#0779BC]/10 rounded-full flex items-center justify-center">
                  <QrCode className="h-6 w-6 text-[#0779BC]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Employee ID</p>
                  <p className="text-lg font-semibold text-gray-900 font-mono">{employee.employee_id}</p>
                </div>
              </div>
              
              {/* Job Title */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-[#0779BC]/10 rounded-full flex items-center justify-center">
                  <Briefcase className="h-6 w-6 text-[#0779BC]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Job Title</p>
                  <p className="text-lg font-semibold text-gray-900">{employee.job_title}</p>
                </div>
              </div>
              
              {/* Company */}
              <div className="flex items-center gap-4 p-4 bg-gray-50 rounded-lg">
                <div className="w-12 h-12 bg-[#0779BC]/10 rounded-full flex items-center justify-center">
                  <Building className="h-6 w-6 text-[#0779BC]" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Company</p>
                  <p className="text-lg font-semibold text-gray-900">{employee.company}</p>
                </div>
              </div>
            </div>
            
            {/* Footer */}
            <div className="border-t p-4 bg-[#0779BC]/5 rounded-b-lg">
              <div className="flex items-center justify-center gap-2 text-[#0779BC]">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">{employee.message || 'Confirmed as authentic'}</span>
              </div>
              <p className="text-center text-xs text-gray-500 mt-2">
                Verified on {new Date().toLocaleDateString()} at {new Date().toLocaleTimeString()}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default VerifyEmployee;
